import {
  buildImageReadApiUrl,
  getAiServicesOrigin,
} from "./mosidaApi";
import { getProductColorLabel } from "./productService";
import {
  getProductStockQuantity,
  shouldSkipStockCheck,
} from "./stockService";
import { toCdnMediaUrl } from "../utils/cdnMediaUrl";
import {
  appendMerchArtworkFlags,
  getMerchGenerateArtworkFlags,
} from "../utils/merchArtworkFlags";

const MERCH_GENERATE_PATH = "/creative-system/merch/generate";
const MERCH_GENERATE_CACHE_PREFIX = "merchGenerateCache:v2:";
/** Creative-system product ids that should be generated with API `pad=true`. */
const STICKER_SHEET_PRODUCT_KEY = "sticker-sheet";
const SINGLE_STICKER_PRODUCT_KEY = "sticker";

function getProductDisplayName(name) {
  if (typeof name === "string") return name.trim();
  if (name && typeof name === "object") {
    const nested = name.name;
    if (typeof nested === "string") return nested.trim();
  }
  return "";
}

/** Single sticker, N-stickers packs, and sticker sheet use padded generate. */
function isPaddedStickerProductKey(productKey) {
  if (!productKey) return false;
  if (productKey === STICKER_SHEET_PRODUCT_KEY) return true;
  if (productKey === SINGLE_STICKER_PRODUCT_KEY) return true;
  return /^stickers-\d+$/.test(productKey);
}

/** In-memory cache for the current kiosk tab (faster than sessionStorage). */
const merchGenerateMemoryCache = new Map();

function slugifyToken(value) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Map catalog merchandise to creative-system merch product ids
 * (e.g. "T Shirt" → "tshirt", "Poster" → "canvas").
 */
export function resolveMerchGenerateProductKey(product) {
  const name = getProductDisplayName(product?.name).toLowerCase();
  const sku = String(product?.sku_code || "")
    .trim()
    .toLowerCase();
  const haystack = `${name} ${sku}`.trim();
  if (!haystack) return null;

  if (haystack.includes("sticker sheet") || sku === "sts") return "sticker-sheet";
  const stickersCountMatch = name.match(/\b(\d+)\s*stickers?\b/);
  if (stickersCountMatch) {
    return `stickers-${stickersCountMatch[1]}`;
  }
  if (name.includes("stickers")) {
    const countFromName = name.match(/(\d+)/);
    if (countFromName) return `stickers-${countFromName[1]}`;
  }
  if (haystack.includes("sticker") || sku === "st") return "sticker";

  if (haystack.includes("hoodie") || sku === "hd" || sku.startsWith("hd")) {
    return "hoodie";
  }
  if (
    haystack.includes("t shirt") ||
    haystack.includes("t-shirt") ||
    haystack.includes("tshirt") ||
    sku === "ts" ||
    sku.startsWith("ts")
  ) {
    return "tshirt";
  }
  if (haystack.includes("tote") || sku === "tb" || sku.startsWith("tb")) {
    return "tote";
  }
  if (/\bcap\b/.test(haystack) || sku === "cp" || sku.startsWith("cp")) {
    return "cap";
  }
  if (
    haystack.includes("photo paper") ||
    haystack.includes("photo print") ||
    haystack.includes("photopaper") ||
    sku === "ppp" ||
    sku === "pp" ||
    sku.startsWith("pp")
  ) {
    return "photo-print";
  }
  if (
    haystack.includes("poster") ||
    haystack.includes("canvas") ||
    sku === "ps" ||
    sku === "ca" ||
    sku.startsWith("ca")
  ) {
    return "canvas";
  }

  const slug = slugifyToken(name);
  return slug || null;
}

/** Catalog labels → hex keys the merch/generate API expects. */
const MERCH_GENERATE_COLOR_HEX = {
  ash: "#b2b2b2",
};

function toMerchGenerateColorKey(label) {
  const key = String(label || "")
    .trim()
    .toLowerCase();
  if (!key) return "";
  return MERCH_GENERATE_COLOR_HEX[key] || key;
}

function normalizeMerchColorKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^#/, "");
}

/** Match catalog color labels to generate item colors (e.g. ash ↔ #b2b2b2). */
function merchColorsMatch(catalogLabel, generateColor) {
  const left = normalizeMerchColorKey(catalogLabel);
  const right = normalizeMerchColorKey(generateColor);
  if (!left || !right) return false;
  if (left === right) return true;
  const leftHex = normalizeMerchColorKey(toMerchGenerateColorKey(left));
  const rightHex = normalizeMerchColorKey(toMerchGenerateColorKey(right));
  return leftHex === right || rightHex === left || leftHex === rightHex;
}

export function collectProductColorNames(product) {
  const colors = Array.isArray(product?.color) ? product.color : [];
  const seen = new Set();
  const out = [];

  colors.forEach((color) => {
    const label = getProductColorLabel(color).toLowerCase();
    if (!label) return;
    const generateKey = toMerchGenerateColorKey(label);
    if (!generateKey || seen.has(generateKey)) return;
    seen.add(generateKey);
    out.push(generateKey);
  });

  return out;
}

/** Build products[] + colors{} from the merchandise catalog response. */
export function buildMerchGeneratePayload(products) {
  const productKeys = [];
  const colors = {};
  const seen = new Set();

  (products || []).forEach((product) => {
    const key = resolveMerchGenerateProductKey(product);
    if (!key || seen.has(key)) return;
    seen.add(key);
    productKeys.push(key);

    const colorNames = collectProductColorNames(product);
    if (colorNames.length > 0) {
      colors[key] = colorNames;
    }
  });

  return { products: productKeys, colors };
}

async function fetchArtworkResponse(url) {
  const trimmed = String(url || "").trim();
  if (!trimmed) throw new Error("Artwork URL is empty");

  if (/^(data:|blob:)/i.test(trimmed)) {
    return fetch(trimmed);
  }

  // CloudFront/S3 has no CORS for the kiosk origin — always read via API proxy.
  if (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.includes("/api/image-processing/read?url=")
  ) {
    const proxied = trimmed.includes("/api/image-processing/read?url=")
      ? trimmed
      : buildImageReadApiUrl(trimmed);
    return fetch(proxied, {
      method: "GET",
      headers: { accept: "image/*" },
    });
  }

  return fetch(trimmed);
}

async function artworkToFile(artwork) {
  if (artwork instanceof File) return artwork;
  if (artwork instanceof Blob) {
    return new File([artwork], "artwork.png", {
      type: artwork.type || "image/png",
    });
  }

  const response = await fetchArtworkResponse(artwork);
  if (!response.ok) {
    throw new Error(`Failed to fetch artwork (${response.status})`);
  }
  const blob = await response.blob();
  const type = blob.type || "image/png";
  const ext = type.includes("jpeg") || type.includes("jpg") ? "jpg" : "png";
  return new File([blob], `artwork.${ext}`, { type });
}

function artworkCacheToken(artwork) {
  if (typeof artwork === "string") {
    const trimmed = artwork.trim();
    if (trimmed.startsWith("data:")) {
      return `data:${trimmed.length}:${trimmed.slice(0, 96)}:${trimmed.slice(-48)}`;
    }
    return trimmed;
  }
  if (artwork instanceof File) {
    return `file:${artwork.name}:${artwork.size}:${artwork.lastModified}`;
  }
  return `blob:${artwork.size}:${artwork.type}`;
}

export function buildMerchGenerateCacheKey(
  artwork,
  products,
  station = "merch",
  isTransparent = true,
  isLogo = false
) {
  const { products: productKeys, colors } = buildMerchGeneratePayload(products);
  const hasStickerPad = productKeys.some(isPaddedStickerProductKey);
  return [
    artworkCacheToken(artwork),
    station,
    isTransparent ? "transparent" : "opaque",
    isLogo ? "logo" : "no-logo",
    hasStickerPad ? "sticker-pad" : "no-sticker-pad",
    productKeys.join(","),
    JSON.stringify(colors),
  ].join("::");
}

function readMerchGenerateCache(cacheKey) {
  const fromMemory = merchGenerateMemoryCache.get(cacheKey);
  if (fromMemory?.success && fromMemory.items?.length) return fromMemory;

  try {
    const raw = sessionStorage.getItem(MERCH_GENERATE_CACHE_PREFIX + cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed?.success ||
      !Array.isArray(parsed.items) ||
      parsed.items.length === 0
    ) {
      return null;
    }
    merchGenerateMemoryCache.set(cacheKey, parsed);
    return parsed;
  } catch {
    return null;
  }
}

/** Sync cache lookup — used to restore merch grid immediately on back navigation. */
export function getCachedMerchGenerateResponse(
  artwork,
  products,
  station = "merch",
  isTransparent = true,
  isLogo = false
) {
  return readMerchGenerateCache(
    buildMerchGenerateCacheKey(
      artwork,
      products,
      station,
      isTransparent,
      isLogo
    )
  );
}

const PRINT_FRAME_MERCH_PAGE_CACHE_KEY = "printFrameMerchPageCache:v1";

export function readPrintFrameMerchPageCache(artKey) {
  const key = String(artKey || "").trim();
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(PRINT_FRAME_MERCH_PAGE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed?.artKey !== key ||
      !Array.isArray(parsed.merchandise) ||
      parsed.merchandise.length === 0
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writePrintFrameMerchPageCache(cache) {
  if (!cache?.artKey?.trim() || !cache.merchandise?.length) return;
  try {
    sessionStorage.setItem(
      PRINT_FRAME_MERCH_PAGE_CACHE_KEY,
      JSON.stringify(cache)
    );
  } catch {
    /* ignore quota */
  }
}

export function clearPrintFrameMerchPageCache() {
  try {
    sessionStorage.removeItem(PRINT_FRAME_MERCH_PAGE_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function writeMerchGenerateCache(cacheKey, response) {
  if (!response.success || !response.items?.length) return;
  merchGenerateMemoryCache.set(cacheKey, response);
  try {
    sessionStorage.setItem(
      MERCH_GENERATE_CACHE_PREFIX + cacheKey,
      JSON.stringify({
        success: true,
        message: response.message,
        items: response.items,
        merch_count: response.merch_count,
        image_url: response.image_url,
      })
    );
  } catch {
    // Quota / private mode — memory cache still works for this session.
  }
}

/** Warm the browser image cache so mockups paint quickly on the grid. */
export function preloadMerchGenerateImages(items) {
  if (!Array.isArray(items)) return;
  const seen = new Set();
  items.forEach((item) => {
    const url = typeof item?.image_url === "string" ? item.image_url.trim() : "";
    if (!url || seen.has(url)) return;
    seen.add(url);
    const img = new Image();
    img.decoding = "async";
    img.src = url;
  });
}

export function clearMerchGenerateCache() {
  merchGenerateMemoryCache.clear();
  clearPrintFrameMerchPageCache();
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(MERCH_GENERATE_CACHE_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/**
 * Merge generate API `items` into catalog products so list/modals show mockup images.
 * Sets `merchGeneratedMockup` so UI can skip a second artwork overlay.
 */
export function applyMerchGenerateItemsToProducts(products, items) {
  if (!Array.isArray(products) || products.length === 0) return products;
  if (!Array.isArray(items) || items.length === 0) return products;

  const byKey = new Map();
  items.forEach((item) => {
    const key = String(item?.id || "")
      .trim()
      .toLowerCase();
    const imageUrl =
      typeof item?.image_url === "string"
        ? toCdnMediaUrl(item.image_url.trim())
        : "";
    if (!key || !imageUrl) return;
    const list = byKey.get(key) || [];
    list.push({ ...item, image_url: imageUrl });
    byKey.set(key, list);
  });

  return products.map((product) => {
    const key = resolveMerchGenerateProductKey(product);
    if (!key) return product;
    const matched = byKey.get(key);
    if (!matched?.length) return product;

    const defaultImage =
      matched.find((item) => !item.color)?.image_url?.trim() ||
      matched[0]?.image_url?.trim() ||
      "";

    const nextColors = Array.isArray(product.color)
      ? product.color.map((color) => {
          if (color == null) return color;
          const label = getProductColorLabel(color).toLowerCase();
          if (!label) return color;

          const colorMatch = matched.find((item) =>
            merchColorsMatch(label, item.color || item.color_label)
          );
          const imageUrl = colorMatch?.image_url?.trim();
          if (!imageUrl) return color;

          if (typeof color === "string") {
            return {
              name: color,
              productImageUrl: imageUrl,
              merchGeneratedMockup: true,
            };
          }
          return {
            ...color,
            productImageUrl: imageUrl,
            product_image_url: imageUrl,
            merchGeneratedMockup: true,
          };
        })
      : product.color;

    // Tote → beige; hoodie → ash if available; apparel → white → black → any.
    const toteBeigeLabels = new Set(["beige", "cream", "natural", "ivory"]);
    const preferredLabels =
      key === "tote"
        ? ["beige", "cream", "natural", "ivory"]
        : key === "hoodie"
          ? ["ash", "white", "black"]
          : ["white", "black"];
    let preferredColorImage = "";
    if (Array.isArray(nextColors)) {
      if (key === "tote") {
        const beigeMatch = nextColors.find((color) => {
          if (!color || typeof color !== "object") return false;
          const label = getProductColorLabel(color).trim().toLowerCase();
          if (!toteBeigeLabels.has(label)) return false;
          return Boolean(
            String(color.productImageUrl || color.product_image_url || "").trim()
          );
        });
        if (beigeMatch) {
          preferredColorImage = String(
            beigeMatch.productImageUrl || beigeMatch.product_image_url || ""
          ).trim();
        }
      }

      if (!preferredColorImage) {
        for (const preferredLabel of preferredLabels) {
          const match = nextColors.find((color) => {
            if (!color || typeof color !== "object") return false;
            const label = getProductColorLabel(color).trim().toLowerCase();
            if (label !== preferredLabel) return false;
            const stock = getProductStockQuantity(product, {
              colorLabel: getProductColorLabel(color),
            });
            const available =
              shouldSkipStockCheck(product) || stock == null || stock > 0;
            const image = String(
              color.productImageUrl || color.product_image_url || ""
            ).trim();
            return available && Boolean(image);
          });
          if (match) {
            preferredColorImage = String(
              match.productImageUrl || match.product_image_url || ""
            ).trim();
            break;
          }
        }
      }

      if (!preferredColorImage) {
        preferredColorImage =
          nextColors
            .map((color) => {
              if (!color || typeof color !== "object") return "";
              if (key === "tote") {
                const label = getProductColorLabel(color).trim().toLowerCase();
                if (toteBeigeLabels.has(label)) return "";
              }
              const stock = getProductStockQuantity(product, {
                colorLabel: getProductColorLabel(color),
              });
              const available =
                shouldSkipStockCheck(product) || stock == null || stock > 0;
              if (!available) return "";
              return String(
                color.productImageUrl || color.product_image_url || ""
              ).trim();
            })
            .find(Boolean) || "";
      }
    }

    const firstColorImage = Array.isArray(nextColors)
      ? nextColors
          .map((color) =>
            color && typeof color === "object"
              ? String(color.productImageUrl || "").trim()
              : ""
          )
          .find(Boolean)
      : "";

    const displayImage = preferredColorImage || defaultImage || firstColorImage;
    if (!displayImage) return product;

    return {
      ...product,
      color: nextColors,
      picture: displayImage,
      imgURL: displayImage,
      image_url: displayImage,
      merchGeneratedMockup: true,
    };
  });
}

function colorsForProductKeys(colors, productKeys) {
  const out = {};
  productKeys.forEach((key) => {
    if (Array.isArray(colors[key]) && colors[key].length) {
      out[key] = colors[key];
    }
  });
  return out;
}

async function requestMerchGenerate(options) {
  const form = new FormData();
  form.append("artwork", options.artworkFile);
  form.append("station", options.station);
  form.append("products", JSON.stringify(options.productKeys));
  form.append("colors", JSON.stringify(options.colors));
  appendMerchArtworkFlags(form, {
    include: options.includeArtworkFlags,
    is_logo: options.isLogo,
    is_transparent: options.isTransparent,
  });
  if (options.pad) {
    form.append("pad", "true");
  }

  const response = await fetch(`${options.base}${MERCH_GENERATE_PATH}`, {
    method: "POST",
    body: form,
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    return {
      success: false,
      error:
        data?.error ||
        data?.message ||
        `Merch generate failed (${response.status})`,
      items: data?.items,
    };
  }

  return {
    success: Boolean(data?.success ?? true),
    message: data?.message,
    error: data?.error,
    items: Array.isArray(data?.items) ? data.items : [],
    merch_count: data?.merch_count,
    image_url: data?.image_url,
  };
}

/**
 * Merch mockup generation with memory + sessionStorage cache.
 * Same artwork + products reuses cached image URLs (no second generate call).
 * One POST for the full catalog. When sticker products are included, send
 * `pad=true` so stickers get padded artwork (API accepts mixed product lists).
 */
export async function generateMerchMockups(options) {
  const station = options.station || "merch";
  const artworkFlags = getMerchGenerateArtworkFlags(options.artworkType);
  const isTransparent =
    typeof options.isTransparent === "boolean"
      ? options.isTransparent
      : artworkFlags.is_transparent;
  const isLogo =
    typeof options.isLogo === "boolean"
      ? options.isLogo
      : artworkFlags.is_logo;
  const includeArtworkFlags = Boolean(artworkFlags.include);
  const base = getAiServicesOrigin().replace(/\/+$/, "");
  if (!base) {
    console.warn(
      "[merchGenerate] missing AI origin — set REACT_APP_AI_SERVICES_ORIGIN in .env"
    );
    return {
      success: false,
      error:
        "AI services origin is not configured (REACT_APP_AI_SERVICES_ORIGIN)",
    };
  }
  console.info("[merchGenerate] POST", `${base}${MERCH_GENERATE_PATH}`, {
    artworkType: options.artworkType || null,
    includeFlags: includeArtworkFlags,
    is_logo: isLogo,
    is_transparent: isTransparent,
  });

  const { products: productKeys, colors } = buildMerchGeneratePayload(
    options.products
  );

  if (!productKeys.length) {
    return { success: false, error: "No merch products to generate" };
  }

  const cacheKey = buildMerchGenerateCacheKey(
    options.artwork,
    options.products,
    station,
    isTransparent,
    isLogo
  );

  if (!options.forceRefresh) {
    const cached = readMerchGenerateCache(cacheKey);
    if (cached) {
      preloadMerchGenerateImages(cached.items);
      return { ...cached, message: cached.message || "Loaded from cache" };
    }
  }

  const artworkFile = await artworkToFile(options.artwork);
  const pad = productKeys.some(isPaddedStickerProductKey);

  const response = await requestMerchGenerate({
    base,
    artworkFile,
    station,
    productKeys,
    colors: colorsForProductKeys(colors, productKeys),
    includeArtworkFlags,
    isTransparent,
    isLogo,
    pad,
  });

  if (!response.success) {
    return response;
  }

  const items = Array.isArray(response.items) ? response.items : [];
  const result = {
    success: items.length > 0,
    message: response.message,
    error: response.error,
    items,
    merch_count: items.length || response.merch_count,
    image_url: response.image_url,
  };

  if (result.success && result.items?.length) {
    writeMerchGenerateCache(cacheKey, result);
    preloadMerchGenerateImages(result.items);
  }

  return result;
}
